import { useCallback, useRef, useState } from 'react';

// Declare window.IGLOO type
declare global {
  interface Window {
    IGLOO?: {
      getBlackbox?: () => {
        finished: boolean;
        blackbox: string;
      };
      trace_handler?: (message: string) => void;
      enable_rip?: boolean;
      enable_flash?: boolean;
      install_flash?: boolean;
      loader?: {
        version: string;
        fp_static: boolean;
        tp: boolean;
        tp_host: string;
      };
    };
  }
}

// Track if iovation script has been loaded globally
let iovationScriptLoaded = false;
let iovationScriptLoading = false;

/**
 * Dynamically load the iovation script
 * @returns Promise that resolves when script is loaded
 */
const loadIovationScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Already loaded
    if (iovationScriptLoaded) {
      resolve();
      return;
    }

    // Currently loading, wait for it
    if (iovationScriptLoading) {
      const checkLoaded = setInterval(() => {
        if (iovationScriptLoaded) {
          clearInterval(checkLoaded);
          resolve();
        }
      }, 50);
      return;
    }

    iovationScriptLoading = true;

    // Initialize IGLOO config before loading script
    window.IGLOO = window.IGLOO || {
      enable_rip: true,
      enable_flash: false,
      install_flash: false,
      loader: {
        version: 'general5',
        fp_static: true,
        tp: true,
        tp_host: 'https://mpsnare.iesnare.com',
      },
    };

    // Create and inject script element
    const script = document.createElement('script');
    script.src = '/static/js/iovation.js';
    script.async = true;

    script.onload = () => {
      iovationScriptLoaded = true;
      iovationScriptLoading = false;
      resolve();
    };

    script.onerror = () => {
      iovationScriptLoading = false;
      reject(new Error('Failed to load iovation script'));
    };

    document.body.appendChild(script);
  });
};

/**
 * Custom hook to handle iovation fingerprinting
 * Lazily loads the iovation script only when initializeIovation is called
 * @returns Object with initialization function and ready state
 */
export const useIovationFingerprint = () => {
  const [fingerprintReady, setFingerprintReady] = useState(false);
  const fingerprintSent = useRef(false);

  // Send fingerprint to backend
  const sendFingerprintToBackend = async (fingerprint: string) => {
    if (fingerprintSent.current) return;

    try {
      const response = await fetch(
        // '/app_api/iovation/set_iovation_fingerprint',
        '/shared-api/iovation/set-fingerprint',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ fingerprint }),
          credentials: 'include', // Important for cookies
        }
      );

      const data = await response.json();

      if (response.ok) {
        fingerprintSent.current = true;
        setFingerprintReady(true);
      } else {
        console.error('Failed to store iovation fingerprint:', data);
      }
    } catch (error) {
      console.error('Error sending iovation fingerprint to backend:', error);
    }
  };

  // Generate iovation fingerprint with Fibonacci-like backoff
  const generateFingerprint = () => {
    let previousDelay = 0;
    let currentDelay = 10;
    const maxTotalTime = 10000; // 10 seconds
    let elapsedTime = 0;

    const getNextDelay = () => {
      const nextDelay = previousDelay + currentDelay;
      previousDelay = currentDelay;
      currentDelay = Math.min(nextDelay, 1000); // Cap individual delay at 1 second
      return currentDelay;
    };

    const attemptGeneration = () => {
      if (!window.IGLOO) {
        if (elapsedTime < maxTotalTime) {
          const delay = getNextDelay();
          elapsedTime += delay;
          setTimeout(attemptGeneration, delay);
        } else {
          console.error('iovation failed to load after max time');
        }
        return;
      }

      if (typeof window.IGLOO.getBlackbox !== 'function') {
        if (elapsedTime < maxTotalTime) {
          const delay = getNextDelay();
          elapsedTime += delay;
          setTimeout(attemptGeneration, delay);
        } else {
          console.error(
            'iovation getBlackbox failed to initialize after max time'
          );
        }
        return;
      }

      const bbData = window.IGLOO.getBlackbox();

      if (bbData.finished) {
        const blackBoxString = bbData.blackbox;
        const encodedBlackBox = encodeURIComponent(blackBoxString);

        sendFingerprintToBackend(blackBoxString);
      } else {
        if (elapsedTime < maxTotalTime) {
          const delay = getNextDelay();
          elapsedTime += delay;
          setTimeout(attemptGeneration, delay);
        } else sendFingerprintToBackend(bbData.blackbox);
      }
    };

    setTimeout(attemptGeneration, 10);
  };

  /**
   * Initialize iovation fingerprinting
   * Lazily loads the script on first call, then generates fingerprint
   * Call this function when you want to start the fingerprinting process
   */
  const initializeIovation = useCallback(() => {
    if (fingerprintSent.current) return;

    // Lazily load the iovation script, then generate fingerprint
    loadIovationScript()
      .then(() => {
        window.IGLOO = window.IGLOO || {};
        window.IGLOO.trace_handler = function (message) {
          // console.log('iovation trace:', message);
        };

        generateFingerprint();
      })
      .catch((error) => {
        console.error('Error loading iovation:', error);
      });
  }, []);

  return {
    initializeIovation,
    fingerprintReady,
  };
};
